import React, { useState, useEffect, useRef, useCallback } from "react";
import styled from "styled-components";
import { useSelector, useDispatch } from "react-redux";

// @ts-expect-error - JSXファイルの型定義なし
import { VideoManagerProvider } from "../../contexts/VideoManagerContext";
// @ts-expect-error - JSファイルの型定義なし
import { selectIsAudioEnabled, toggleAudio } from "../../app/actions/audio/audioSlice";

// @ts-expect-error - JSXファイルの型定義なし
import OsFooter from "../os/components/OsFooter";
// @ts-expect-error - JSXファイルの型定義なし
import OsAudioToggle from "../os/components/OsAudioToggle";

import CareersHeroTitle from "./components/CareersHeroTitle";
import CareersMessage from "./components/CareersMessage";
import CareersConcept from "./components/CareersConcept";
import CareersJobCard from "./components/CareersJobCard";
import CareersJobModal from "./components/CareersJobModal";

import { FormField, JobConditions, JobPosition, OfficePhoto } from "./types";

// BGM
import careersBgm from "../../assets/audio/careers/careers.mp3";
import adBgm from "../../assets/audio/careers/ad.mp3";
import pmBgm from "../../assets/audio/careers/pm.mp3";
import mrBgm from "../../assets/audio/careers/mr.mp3";
import seBgm from "../../assets/audio/careers/se.mp3";

// ダミー画像（OS用を流用、後で差し替え可能）
import osMcp from "../../assets/images/os/os_mcp.webp";
import osMemberlist from "../../assets/images/os/os_memberlist.webp";
import osMgk from "../../assets/images/os/os_mgk.webp";
import osProfile from "../../assets/images/os/os_profile.webp";

// オフィス写真（AD 詳細用）
import officePhoto1 from "../../assets/images/careers/office_260527_1.webp";
import officePhoto2 from "../../assets/images/careers/office_260527_2.webp";
import officePhoto3 from "../../assets/images/careers/office_260527_3.webp";
import officePhoto4 from "../../assets/images/careers/office_260527_4.webp";
import officePhoto5 from "../../assets/images/careers/office_260527_5.webp";

// ============================================================================
// 全職種共通の定数
// ----------------------------------------------------------------------------
// 雇用形態 / 勤務時間 / 勤務地 / 休日休暇 / 選考スケジュール / 注意事項 は
// 現状すべての職種で共通。AD のみ給与レンジと「海外視察」表記の補足が異なる。
// 共通項目は定数化し、職種ごとには差分だけ書く方針。
// ============================================================================

const COMMON_TAGS = ['正社員', '契約社員', '業務委託'];

// 福利厚生のうち、海外視察の表記だけ職種によって粒度が異なるため、
// 海外視察を挟まない前後でブロックを分けておく。
const BENEFITS_HEAD = [
  '住宅手当：勤務地より徒歩ルート8.5km圏内の場合',
  '賞与：年2回',
  '社会保険完備',
  '交通費支給',
  'オフィス共有施設（ジム）利用可',
];

const BENEFITS_TAIL = [
  'MacBook Pro 支給',
  '副業可',
];

const buildBenefits = (overseasTrip: string): string[] => [
  ...BENEFITS_HEAD,
  overseasTrip,
  ...BENEFITS_TAIL,
];

const COMMON_CONDITIONS: Omit<JobConditions, 'salary' | 'benefits'> = {
  contractTypes: COMMON_TAGS,
  contractNotes: [
    '試用期間 3ヶ月',
    '正社員登用あり',
  ],
  workingHours: 'フレックスタイム（コアタイム13:00-16:00）',
  holidays: [
    '完全週休2日制（土日祝）',
    '年次有給休暇',
    '夏季休暇・年末年始休暇',
  ],
  location: '東京都江東区青海2-7-4 theSOHO 1226',
};

const COMMON_SELECTION_PROCESS = [
  '書類選考（1週間以内にご連絡）',
  'オンラインカジュアル面談',
  '代表面接',
  '内定',
];

// 勤務地（COMMON_CONDITIONS.location）は全職種共通のため、
// オフィス写真も全職種で同じものを掲載する。
const COMMON_OFFICE_PHOTOS: OfficePhoto[] = [
  { src: officePhoto1, label: 'スタジオ風景' },
  { src: officePhoto2, label: '編集室' },
  { src: officePhoto3, label: 'ディレクションスペース' },
  { src: officePhoto4, label: 'コーディングスペース' },
  { src: officePhoto5, label: '休憩スペース' },
];

const COMMON_NOTES = [
  '応募書類はすべてデータ（PDF等）にてご提出ください',
  'ご提出いただいた書類は選考目的以外には使用しません',
  '選考結果はメールにてご連絡します（書類選考：1週間以内目安）',
  '応募書類の返却は行っておりません',
  '採用決定次第、募集を終了いたします。お早めにご応募ください',
  '現在ご就業中の方も配慮のうえ選考を進めますので、お気軽にご応募ください',
];

// 個人情報同意チェック（応募フォーム最終行で全職種共通）
const PRIVACY_AGREE_FIELD: FormField = {
  id: 'agree',
  type: 'checkbox',
  label: '個人情報の取り扱いに同意する',
  placeholder: '個人情報の取り扱いについて同意します',
  required: true,
};

// 募集職種データ
const jobPositions: JobPosition[] = [
  {
    id: 'audio-director',
    title: 'オーディオディレクター',
    summary: '自らも実制作に携わりながら、サウンド開発全体を統括し、品質を支えます。',
    isNew: true,
    description: '自らも実制作に携わりながら、サウンド開発全体を統括し、品質を支えます。',
    roles: [
      'サウンドデザイナー',
      'MAミキサー',
      'オーディオ実装リード',
      'コンポーザー',
      'ミュージックスーパーバイザー',
    ],
    expectedOutcome: 'プロジェクトと真摯に向き合い、期待される品質に応えながら、最良の解釈を創り出し、チームの知見を束ねて全体品質をリードする',
    welcome: [
      '作曲、サウンドデザイン、エンジニアなど、ひとつのスペシャリティを持つ方。',
      '専門性を軸にしながらも、作品全体の体験や品質に関心を持てる方。',
      '学び続ける姿勢を持ち、専門性を拡張していくことを楽しめる方。',
    ],
    tags: COMMON_TAGS,
    conditions: {
      ...COMMON_CONDITIONS,
      salary: '月給：300,000円〜（経験・スキルを考慮のうえ決定）',
      benefits: buildBenefits('海外視察（GDC Festival of Gaming など）'),
    },
    officePhotos: COMMON_OFFICE_PHOTOS,
    selectionProcess: COMMON_SELECTION_PROCESS,
    notes: COMMON_NOTES,
    formFields: [
      { id: 'name', type: 'text', label: '氏名', placeholder: '山田 太郎', required: true },
      { id: 'email', type: 'email', label: 'メールアドレス', placeholder: 'example@email.com', required: true },
      { id: 'tel', type: 'tel', label: '電話番号', placeholder: '090-1234-5678', required: false },
      { id: 'portfolio', type: 'text', label: 'ポートフォリオURL', placeholder: 'https://...', required: false },
      { id: 'skills', type: 'checkboxGroup', label: 'スキル・経験（該当するものを全て選択）', required: false, options: ['Pro Tools', 'Nuendo（Cubase）', 'Studio One', 'Reaper', 'Logic Pro', 'UAD', 'FabFilter', 'iZotope', 'Waves', 'Serum2', 'Omnisphere3', 'Massive X', 'Reaktor', 'Sylenth', 'Soundly', 'Soundminer', 'BaseHead', 'Boom Library', 'Krotos', 'Sound Particles', 'Wwise', 'Unreal Engine', 'Git', 'Perforce', 'ShotGrid', 'Jira', 'Python', 'Shell', 'CSV / JSON'] },
      { id: 'works', type: 'textarea', label: '主な制作実績', placeholder: '過去の制作実績をお聞かせください', required: true },
      { id: 'resume', type: 'file', label: '履歴書・職務経歴書', required: true },
      { id: 'motivation', type: 'textarea', label: '志望動機', placeholder: 'SOREMOで働きたい理由を教えてください', required: true },
      PRIVACY_AGREE_FIELD,
    ],
    image: osMcp,
    audioFile: adBgm,
  },
  {
    id: 'project-manager',
    title: 'プロジェクトマネージャー',
    summary: 'クライアントと多様なスペシャリストの間に立ち、進行、調整、判断を通じて、プロジェクトを確実にゴールに導きます。',
    description: 'クライアントと多様なスペシャリストの間に立ち、進行、調整、判断を通じて、プロジェクトを確実にゴールに導きます。',
    roles: [
      'ミュージックコーディネーター',
      'キャスティングディレクター',
      'タレントソーサー',
      'アーティストマネージャー',
      'コミュニティオーガナイザー',
    ],
    expectedOutcome: 'プロジェクトを俯瞰し、関係者それぞれの意図と制約を整理しながら、美点を引き出し、前向きな進行をリードする',
    welcome: [
      'コミュニケーションスキルを進行管理の基盤として活かしてきた方',
      'まだ見ぬ才能や可能性を見つけ、現場をつなぐ役割にやりがいを感じてきた方',
      '情報の流れやコミュニケーションの摩擦に目を向け、仕組みやUIの改善によって現場をより良くしたい方',
      '専門性を軸にしながらも、作品全体の体験や品質に関心を持てる方。',
    ],
    tags: COMMON_TAGS,
    conditions: {
      ...COMMON_CONDITIONS,
      salary: '月給：300,000円〜（経験・スキルを考慮のうえ決定）',
      benefits: buildBenefits('海外視察（GDC Festival of Gaming など）'),
    },
    officePhotos: COMMON_OFFICE_PHOTOS,
    selectionProcess: COMMON_SELECTION_PROCESS,
    notes: COMMON_NOTES,
    formFields: [
      { id: 'name', type: 'text', label: '氏名', placeholder: '山田 太郎', required: true },
      { id: 'email', type: 'email', label: 'メールアドレス', placeholder: 'example@email.com', required: true },
      { id: 'tel', type: 'tel', label: '電話番号', placeholder: '090-1234-5678', required: true },
      { id: 'skills', type: 'checkboxGroup', label: 'スキル・経験（該当するものを全て選択）', required: false, options: ['音楽制作の進行管理経験', 'アーティストマネジメント経験', 'キャスティング経験', '複数関係者（クライアント／スペシャリスト）との調整経験', '要件や依頼内容の整理・言語化', '不確定な状況での優先順位付け・判断', 'コミュニティ運営'] },
      { id: 'works', type: 'textarea', label: 'これまでの経験', placeholder: 'これまでのプロジェクト経験をお聞かせください', required: true },
      { id: 'resume', type: 'file', label: '履歴書・職務経歴書', required: true },
      { id: 'motivation', type: 'textarea', label: '志望動機', placeholder: 'SOREMOで働きたい理由を教えてください', required: true },
      PRIVACY_AGREE_FIELD,
    ],
    image: osMemberlist,
    audioFile: pmBgm,
  },
  {
    id: 'music-researcher',
    title: 'ミュージックリサーチャー',
    summary: '音楽に関する情報、権利、文脈を専門的に調査し、制作やビジネスの意思決定を支えます。',
    description: '音楽に関する情報、権利、文脈を専門的に調査し、制作やビジネスの意思決定を支えます。',
    roles: [],
    expectedOutcome: '様々な検索、分析ツールを駆使して楽曲を分析し、音楽に関する情報・権利・文脈を整理し、制作や判断において迷いのない選択ができる土台をつくる',
    welcome: [
      '常に新しい音楽を探求し、リスナーとしての広い音楽体験・知識に自信がある方',
      'ゲーム、アニメ、動画など様々なコンテンツに常日頃から触れており、IP知識が豊富な方',
      '基本的な音楽スキルがあり、メロディ、コード、トラックなどを分解してとらえることができる方',
      '新しいテクノロジーによるオーディオ生成、解析に興味がある方',
    ],
    tags: COMMON_TAGS,
    conditions: {
      ...COMMON_CONDITIONS,
      salary: '月給：300,000円〜（経験・スキルを考慮のうえ決定）',
      benefits: buildBenefits('海外視察（GDC Festival of Gaming など）'),
    },
    officePhotos: COMMON_OFFICE_PHOTOS,
    selectionProcess: COMMON_SELECTION_PROCESS,
    notes: COMMON_NOTES,
    formFields: [
      { id: 'name', type: 'text', label: '氏名', placeholder: '山田 太郎', required: true },
      { id: 'email', type: 'email', label: 'メールアドレス', placeholder: 'example@email.com', required: true },
      { id: 'tel', type: 'tel', label: '電話番号', placeholder: '090-1234-5678', required: false },
      { id: 'skills', type: 'checkboxGroup', label: 'スキル・経験（該当するものを全て選択）', required: false, options: ['音楽理論の知識', '楽曲分析経験', 'リサーチ・レポート作成', 'AI/ML音声解析への理解'] },
      { id: 'musicExperience', type: 'textarea', label: '音楽との関わり', placeholder: 'これまでの音楽体験や知識についてお聞かせください', required: true },
      { id: 'resume', type: 'file', label: '履歴書・職務経歴書', required: false },
      { id: 'motivation', type: 'textarea', label: '志望動機', placeholder: 'SOREMOで働きたい理由を教えてください', required: true },
      PRIVACY_AGREE_FIELD,
    ],
    image: osMgk,
    audioFile: mrBgm,
  },
  {
    id: 'software-engineer',
    title: 'ソフトウェアエンジニア',
    summary: 'システムの実装・アップデートを通じて、安定した品質再現性を実現します。',
    description: 'システムの実装・アップデートを通じて、安定した品質再現性を実現します。',
    roles: [],
    expectedOutcome: '実際のプロジェクト課題と向き合いながら、社内に蓄積された知見や判断をシステムとして定着させ、継続的にアップデートされる再現性のある仕組みを構築する',
    welcome: [
      'サウンドプロダクションをテクノロジーでアップデートする意欲のある方',
      '属人的な対応ではなく、知見や判断を仕組みとして残すことに価値を感じる方',
      '作って終わりではなく、運用や改善を前提にシステムを育てていきたい方'
    ],
    skillCategories: [
      {
        title: '開発スタック（SOREMO OS 現在）',
        subcategories: [
          {
            title: 'バックエンド',
            items: [
              'Python / Django',
              'Django REST Framework を用いた API 設計・実装',
              'RDB（MySQL）を用いたデータ設計・運用',
            ],
          },
          {
            title: 'フロントエンド',
            items: [
              'React',
              'Redux Toolkit などによる状態管理',
              'API と連携した UI 実装',
            ],
          },
        ],
      },
      {
        title: '開発・運用に関する理解',
        items: [
          'Git を用いたチーム開発経験',
          '要件変更や運用を前提とした設計・実装の経験',
          '既存コードを読み解き、改善していく姿勢',
        ],
      },
    ],
    tags: COMMON_TAGS,
    conditions: {
      ...COMMON_CONDITIONS,
      salary: '月給：300,000円〜（経験・スキルを考慮のうえ決定）',
      benefits: buildBenefits('海外視察（GDC Festival of Gaming など）'),
    },
    officePhotos: COMMON_OFFICE_PHOTOS,
    selectionProcess: COMMON_SELECTION_PROCESS,
    notes: COMMON_NOTES,
    formFields: [
      { id: 'name', type: 'text', label: '氏名', placeholder: '山田 太郎', required: true },
      { id: 'email', type: 'email', label: 'メールアドレス', placeholder: 'example@email.com', required: true },
      { id: 'github', type: 'text', label: 'GitHub URL', placeholder: 'https://github.com/...', required: false },
      { id: 'portfolio', type: 'text', label: 'ポートフォリオURL', placeholder: 'https://...', required: false },
      { id: 'skills', type: 'checkboxGroup', label: 'スキル・経験（該当するものを全て選択）', required: false, options: ['Python', 'Django', 'Django REST Framework', 'Figma', 'React', 'Redux Toolkit', 'MySQL', 'Amazon RDS', 'Amazon S3', 'AWS EC2', 'AWS Lambda', 'Docker', 'GitHub Actions', 'Cursor', 'Claude Code'] },
      { id: 'experience', type: 'select', label: '開発経験年数', required: true, options: ['1〜2年', '3〜5年', '5〜10年', '10年以上'] },
      { id: 'resume', type: 'file', label: '履歴書・職務経歴書', required: true },
      { id: 'motivation', type: 'textarea', label: '志望動機', placeholder: 'SOREMOで働きたい理由を教えてください', required: true },
      PRIVACY_AGREE_FIELD,
    ],
    image: osProfile,
    audioFile: seBgm,
  },
];

// Styled Components
const CareersMain = styled.main`
  background-color: var(--soremo-background);
  
  /* モバイルでのスクロール最適化 */
  @media (max-width: 768px) {
    -webkit-overflow-scrolling: touch;
    overflow-anchor: none;
  }
`;

const JobsSection = styled.section`
  width: 100%;
  padding: 64px 0;
  background-color: #fff;
  
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const JobsSectionTitle = styled.h2.attrs({
  className: "heading-spacing-2140",
})`
  text-align: center;
  margin-bottom: 16px;
  color: #333;
`;

const JobsSectionSubtitle = styled.p.attrs({
  className: "bodytext-13",
})`
  text-align: center;
  color: #666;
  margin-bottom: 48px;
`;

const JobsGrid = styled.div`
  width: 100%;
  margin-inline: auto;
  
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;

  @media (width > 960px) {
    flex-direction: row;
    flex-wrap: wrap;
  }
`;

interface RootState {
  device: {
    isMobile: boolean;
  };
}

// メインコンテンツコンポーネント
const CareersContent: React.FC = () => {
  const isAudioEnabled = useSelector(selectIsAudioEnabled) as boolean;
  const isMobile = useSelector((state: RootState) => state.device.isMobile);
  const dispatch = useDispatch();
  
  const audioRef = useRef<HTMLAudioElement>(null);
  const jobAudioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isJobAudioPlaying, setIsJobAudioPlaying] = useState(false);
  const heroAreaRef = useRef<HTMLDivElement>(null);
  const [isInHeroArea, setIsInHeroArea] = useState(false);
  
  const [selectedJob, setSelectedJob] = useState<JobPosition | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // スクロール設定
  useEffect(() => {
    document.body.style.overflow = "auto";
    
    if (!isMobile) {
      document.documentElement.style.scrollSnapType = "y proximity";
      document.documentElement.style.scrollBehavior = "smooth";
      document.body.style.scrollSnapType = "y proximity";
    } else {
      document.documentElement.style.scrollBehavior = "auto";
      document.body.style.scrollBehavior = "auto";
      (document.body.style as CSSStyleDeclaration & { webkitOverflowScrolling?: string }).webkitOverflowScrolling = "touch";
    }
    
    return () => {
      document.documentElement.style.scrollSnapType = "";
      document.documentElement.style.scrollBehavior = "";
      document.body.style.scrollSnapType = "";
      (document.body.style as CSSStyleDeclaration & { webkitOverflowScrolling?: string }).webkitOverflowScrolling = "";
    };
  }, [isMobile]);

  // オーディオのON/OFF切り替え
  const handleAudioToggle = useCallback(() => {
    dispatch(toggleAudio());
  }, [dispatch]);

  // フェードイン
  const handleFadeInAudio = useCallback(() => {
    if (!audioRef.current || isPlaying || !isAudioEnabled) return;

    let fadeVolume = 0.0;
    audioRef.current.volume = fadeVolume;
    audioRef.current.muted = !isAudioEnabled;
    audioRef.current.play();
    setIsPlaying(true);

    const fadeInterval = setInterval(() => {
      fadeVolume = fadeVolume + 0.1;
      if (fadeVolume >= 1.0) {
        if (audioRef.current) {
          audioRef.current.volume = 1.0;
        }
        clearInterval(fadeInterval);
      } else if (audioRef.current) {
        audioRef.current.volume = fadeVolume;
      }
    }, 200);
  }, [isPlaying, isAudioEnabled]);

  // フェードアウト
  const handleFadeOutAudio = useCallback(() => {
    if (!audioRef.current || !isPlaying) return;

    let fadeVolume = audioRef.current.volume;
    const fadeInterval = setInterval(() => {
      fadeVolume = fadeVolume - 0.1;
      if (fadeVolume <= 0.0) {
        if (audioRef.current) {
          audioRef.current.volume = 0.0;
          audioRef.current.pause();
        }
        clearInterval(fadeInterval);
        setIsPlaying(false);
      } else if (audioRef.current) {
        audioRef.current.volume = fadeVolume;
      }
    }, 200);
  }, [isPlaying]);

  // 職種専用オーディオのフェードイン
  const handleFadeInJobAudio = useCallback(() => {
    if (!jobAudioRef.current || isJobAudioPlaying || !isAudioEnabled) return;

    let fadeVolume = 0.0;
    jobAudioRef.current.volume = fadeVolume;
    jobAudioRef.current.muted = !isAudioEnabled;
    jobAudioRef.current.play();
    setIsJobAudioPlaying(true);

    const fadeInterval = setInterval(() => {
      fadeVolume = fadeVolume + 0.1;
      if (fadeVolume >= 1.0) {
        if (jobAudioRef.current) {
          jobAudioRef.current.volume = 1.0;
        }
        clearInterval(fadeInterval);
      } else if (jobAudioRef.current) {
        jobAudioRef.current.volume = fadeVolume;
      }
    }, 200);
  }, [isJobAudioPlaying, isAudioEnabled]);

  // 職種専用オーディオのフェードアウト
  const handleFadeOutJobAudio = useCallback(() => {
    if (!jobAudioRef.current || !isJobAudioPlaying) return;

    let fadeVolume = jobAudioRef.current.volume;
    const fadeInterval = setInterval(() => {
      fadeVolume = fadeVolume - 0.1;
      if (fadeVolume <= 0.0) {
        if (jobAudioRef.current) {
          jobAudioRef.current.volume = 0.0;
          jobAudioRef.current.pause();
        }
        clearInterval(fadeInterval);
        setIsJobAudioPlaying(false);
      } else if (jobAudioRef.current) {
        jobAudioRef.current.volume = fadeVolume;
      }
    }, 200);
  }, [isJobAudioPlaying]);

  // ヒーローエリアの検出
  useEffect(() => {
    const observedTarget = heroAreaRef.current;
    if (!("IntersectionObserver" in window) || !observedTarget) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsInHeroArea(entry.isIntersecting);
          
          // モーダル表示中はヒーローBGMの制御をスキップ
          if (isModalOpen) {
            return;
          }
          
          if (entry.isIntersecting && isAudioEnabled) {
            handleFadeInAudio();
          } else if (!entry.isIntersecting) {
            handleFadeOutAudio();
          }
        });
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 0.1,
      },
    );

    observer.observe(observedTarget);

    return () => {
      observer.unobserve(observedTarget);
    };
  }, [handleFadeInAudio, handleFadeOutAudio, isAudioEnabled, isModalOpen]);

  // オーディオ状態が変更されたときにBGMのmuted属性を更新し、ONになったら適切なオーディオを再生
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.muted = !isAudioEnabled;
    }
    if (jobAudioRef.current) {
      jobAudioRef.current.muted = !isAudioEnabled;
    }
    
    // オーディオがONになったとき、現在の状態に応じて適切なオーディオを再生開始
    if (isAudioEnabled) {
      // モーダル表示中で職種専用オーディオがある場合
      if (isModalOpen && selectedJob?.audioFile && !isJobAudioPlaying) {
        handleFadeInJobAudio();
      }
      // モーダルが閉じている、または職種専用オーディオがない場合で、ヒーロー領域にいる場合
      else if (!isModalOpen && isInHeroArea && !isPlaying) {
        handleFadeInAudio();
      }
    }
  }, [isAudioEnabled, isModalOpen, selectedJob, isJobAudioPlaying, isInHeroArea, isPlaying, handleFadeInJobAudio, handleFadeInAudio]);

  // 職種カードクリック
  const handleJobClick = useCallback((job: JobPosition) => {
    setSelectedJob(job);
    setIsModalOpen(true);
    
    // 職種専用オーディオがある場合、ヒーローBGMをフェードアウトして切り替え
    if (job.audioFile) {
      handleFadeOutAudio();
      // BGMフェードアウト完了後に職種専用オーディオをフェードイン
      setTimeout(() => {
        handleFadeInJobAudio();
      }, 2200); // フェードアウト完了を待つ (200ms × 10 + バッファ)
    }
  }, [handleFadeOutAudio, handleFadeInJobAudio]);

  // モーダルを閉じる
  const handleCloseModal = useCallback(() => {
    // 職種専用オーディオが再生中の場合、フェードアウトしてヒーローBGMに戻す
    if (selectedJob?.audioFile && isJobAudioPlaying) {
      handleFadeOutJobAudio();
      // 職種専用オーディオフェードアウト完了後、ヒーローエリア内ならBGMを再開
      setTimeout(() => {
        if (isInHeroArea && isAudioEnabled) {
          handleFadeInAudio();
        }
      }, 2200); // フェードアウト完了を待つ
    }
    
    setIsModalOpen(false);
    setSelectedJob(null);
  }, [selectedJob, isJobAudioPlaying, handleFadeOutJobAudio, isInHeroArea, isAudioEnabled, handleFadeInAudio]);

  return (
    <CareersMain className="u-col-center u-bg-background">
      <div ref={heroAreaRef} className="u-w100 u-col-center">
        <CareersHeroTitle />
        <CareersMessage />
        <CareersConcept />
      </div>
      
      <audio ref={audioRef} src={careersBgm} loop />
      {selectedJob?.audioFile && (
        <audio ref={jobAudioRef} src={selectedJob.audioFile} loop />
      )}

      <JobsSection>
        <div className="u-wrapper">
          <JobsSectionTitle>募集職種</JobsSectionTitle>
          <JobsSectionSubtitle>OPEN POSITIONS</JobsSectionSubtitle>
          
          <JobsGrid>
            {jobPositions.map((job) => (
              <CareersJobCard
                key={job.id}
                job={job}
                onClick={() => handleJobClick(job)}
              />
            ))}
          </JobsGrid>
        </div>
      </JobsSection>

      <OsFooter />

      {/* フローティングオーディオボタン */}
      <OsAudioToggle
        isPlaying={isAudioEnabled}
        onToggle={handleAudioToggle}
        isVisible={isMobile ? isInHeroArea : true}
      />

      {/* 全画面モーダル */}
      {selectedJob && (
        <CareersJobModal
          job={selectedJob}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          isAudioEnabled={isAudioEnabled}
          onAudioToggle={handleAudioToggle}
        />
      )}
    </CareersMain>
  );
};

// ラッパーコンポーネント
const Careers: React.FC = () => {
  return (
    <VideoManagerProvider>
      <CareersContent />
    </VideoManagerProvider>
  );
};

export default Careers;
